import { ParcelamosTudo3DS } from "@parcelamostudo-tech/lib-3ds-client";
import { useWhitelabel } from "hooks";
import usePayment, { ICard, ICreditCard } from "hooks/usePayment";
import { useMutation } from "react-query";
import {
  ApiResponseError,
  EnumErrorCode,
  getApiErrorMessages,
} from "services/errors";
import PublicBffService from "../../../services/PublicBff";
import { toast } from "react-toastify";
import { IInstallments } from "models";
import { EnumOriginPayment, getInstallments } from "services/CreditCard";
import { useInstallments } from "./useInstallment";
import { isCard } from "../utils";
import { useState } from "react";

const TOAST_3DS_ID = "toast.payment.threeds";
const TOAST_3DS_AUTO_CLOSE = 3000;
const NUMBER_MULTIPLE_PAYMENT = 2;

interface ThreeDSValidationParams {
  token: string;
  goToResume?: () => void;
}

export function use3DSValidation({
  token = "",
  goToResume,
}: ThreeDSValidationParams) {
  const {
    installments,
    paymentMethods,
    client,
    updateInstallments,
    set3DSMethod,
  } = usePayment();
  const { services, userIdWhitelabel } = useWhitelabel();
  const { debits, chargeMethods } = useInstallments();
  const [isLoading, setLoading] = useState(false);

  const { mutateAsync: mutateCreateCheckoutOrder3ds } = useMutation({
    mutationFn: PublicBffService.createCheckoutOrder3ds,
    onError: handle3DSError,
    onSettled: () => {
      setLoading(false);
    },
    retry: false,
  });

  async function handle3DSError(error: unknown) {
    const responseError = (error as any).response.data as ApiResponseError;

    if (responseError.error === EnumErrorCode.CHARGE_THREE_DS_INVALID_CARD) {
      toast.update(TOAST_3DS_ID, {
        render: "Cartão inválido ou ineligível para 3DS!",
        autoClose: TOAST_3DS_AUTO_CLOSE,
        type: "error",
        isLoading: false,
      });
      return;
    }

    if (responseError.error === EnumErrorCode.CHARGE_CHARGE_OPTIONS_EXPIRED) {
      toast.update(TOAST_3DS_ID, {
        render: "Tempo para prosseguir expirou, tente novamente!",
        autoClose: TOAST_3DS_AUTO_CLOSE,
        type: "error",
        isLoading: false,
      });

      const isMultiplePayment =
        paymentMethods.length === NUMBER_MULTIPLE_PAYMENT;

      const newInstallments = await getInstallments({
        data: {
          origin: EnumOriginPayment.ONLINE,
          debits,
          ...(isMultiplePayment
            ? {
              charge_methods: chargeMethods,
            }
            : {}),
        },
      });

      updateInstallments(newInstallments);

      return;
    }

    toast.update(TOAST_3DS_ID, {
      render: getApiErrorMessages({ error: responseError.error as EnumErrorCode }),
      type: "error",
      autoClose: TOAST_3DS_AUTO_CLOSE,
      isLoading: false
    });
  }

  function buildCardData(method: ICard | ICreditCard) {
    const [month, year] = method.due_date.split("/");

    return {
      exp_month: month,
      exp_year: year,
      holder_name: method.name,
      number: method.number,
      security_code: method.cvv,
    };
  }

  function getChargeOptionId(
    method: ICard | ICreditCard,
    index: number,
    installments: IInstallments
  ) {
    const isMultiple = paymentMethods.length === NUMBER_MULTIPLE_PAYMENT;

    if (isMultiple) {
      return installments?.multiple?.options[index]?.id_charge_option;
    }

    const cardType =
      method.typeMethod === "credit" ? "credit_card" : "debit_card";
    const installmentsNumber =
      cardType === "credit_card" ? (method as ICreditCard).installments - 1 : 0;

    return installments[cardType][installmentsNumber]?.id_charge_option;
  }

  async function initGet3DSValidation(installmentsParam?: IInstallments) {
    const haveCard = paymentMethods.some(isCard);

    if (!services.run_three_ds || !haveCard) {
      goToResume?.();
      return;
    }

    setLoading(true);
    toast.loading("Verificando cartão(s)...", { toastId: TOAST_3DS_ID });

    const selectedInstallments = installmentsParam ?? installments;

    const charges = paymentMethods.reduce<
      Array<{ id_charge_option: string; card: any }>
    >((acc, method, index) => {
      if (!isCard(method)) return acc;

      const card = buildCardData(method);
      const id_charge_option = getChargeOptionId(
        method,
        index,
        selectedInstallments
      );

      if (id_charge_option) {
        acc.push({ id_charge_option, card });
      }

      return acc;
    }, []);

    const payload = {
      id_client: client.id_client,
      charges,
    };

    const result = await mutateCreateCheckoutOrder3ds({
      payload,
      id_establishment: userIdWhitelabel,
      token,
    });

    if (Array.isArray(result)) {
      for (let i = 0; i < result.length; i++) {
        const validation = result[i];
        if (!validation.error && validation.validation_method_token) {
          const parcelamos3DS = new ParcelamosTudo3DS();
          await parcelamos3DS.executeCustomerValidation({
            id_three_ds: validation.id_three_ds,
            validation_method_url: validation.validation_method_url!,
            validation_method_token: validation.validation_method_token,
          });
        }
        set3DSMethod(validation, i);
      }

      if (result.every((validation: any) => !validation.error)) {
        toast.dismiss(TOAST_3DS_ID);
        toast.info("Cartões verificados", {
          autoClose: TOAST_3DS_AUTO_CLOSE,
        });

        goToResume?.();
        return;
      }

      result.forEach((validation: any) => {
        const isErrorInvalidCard =
          validation.code === EnumErrorCode.CHARGE_THREE_DS_INVALID_CARD;
        if (validation.error && isErrorInvalidCard) {
          toast.error(
            `Cartão inválido ou ineligível para 3DS de final ${validation?.last_four_digits}`,
            {
              autoClose: TOAST_3DS_AUTO_CLOSE,
            }
          );
        }
      });
    } else {
      if (!result.error && result.validation_method_token) {
        const parcelamos3DS = new ParcelamosTudo3DS();
        await parcelamos3DS.executeCustomerValidation({
          id_three_ds: result.id_three_ds,
          validation_method_url: result.validation_method_url!,
          validation_method_token: result.validation_method_token,
        });
      }

      set3DSMethod(result, 0);

      const isErrorInvalidCard =
        result?.code === EnumErrorCode.CHARGE_THREE_DS_INVALID_CARD;
      if (result?.error && isErrorInvalidCard) {
        toast.error(`Cartão inválido ou ineligível para 3DS!`, {
          autoClose: TOAST_3DS_AUTO_CLOSE,
        });
        return;
      }

      toast.dismiss(TOAST_3DS_ID);
      toast.info("Cartões verificados", {
        autoClose: TOAST_3DS_AUTO_CLOSE,
      });
      goToResume?.();
    }
    setLoading(false);
  }

  return {
    initGet3DSValidation,
    isLoading,
  };
}
